import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarClock, Check } from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Checkbox } from '@meno/ui/components/ui/checkbox'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@meno/ui/components/ui/dialog'
import type { RowSchedule } from '@/lib/rowSchedule'

// ISO (UTC) ↔ 'YYYY-MM-DDTHH:mm' (local) conversion for datetime-local inputs.
function isoToLocalInput(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
function localInputToIso(local: string): string {
  return new Date(local).toISOString()
}

// Per-row scheduling dialog shared by Playlist and Layout. "Show all the time"
// is the default — unchecking it reveals start + end inputs that define the
// active window. The window is stored on the backend (ISO-UTC) by the caller;
// this dialog converts to/from datetime-local input format.
export function RowScheduleDialog({
  open, rowLabel, value, onClose, onSave,
}: {
  open: boolean
  rowLabel: string
  value: RowSchedule
  onClose: () => void
  onSave: (next: RowSchedule) => void
}) {
  const { t } = useTranslation()

  const defaultWindow = useMemo(() => {
    const now = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    const end = new Date(now.getTime() + 60 * 60 * 1000)
    return { start: fmt(now), end: fmt(end) }
  }, [open])

  const [alwaysOn, setAlwaysOn] = useState(value.alwaysOn)
  const [startAt, setStartAt]   = useState(isoToLocalInput(value.startAt) || defaultWindow.start)
  const [endAt,   setEndAt]     = useState(isoToLocalInput(value.endAt)   || defaultWindow.end)

  useEffect(() => {
    if (!open) return
    setAlwaysOn(value.alwaysOn)
    setStartAt(isoToLocalInput(value.startAt) || defaultWindow.start)
    setEndAt(isoToLocalInput(value.endAt) || defaultWindow.end)
  }, [open, value, defaultWindow])

  const rangeInvalid = !alwaysOn && !!startAt && !!endAt &&
    new Date(endAt).getTime() <= new Date(startAt).getTime()
  const canSave = alwaysOn || (!!startAt && !!endAt && !rangeInvalid)

  const submit = () => {
    if (!canSave) return
    onSave(alwaysOn
      ? { alwaysOn: true }
      : { alwaysOn: false, startAt: localInputToIso(startAt), endAt: localInputToIso(endAt) })
  }

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-primary" />
            {t('playlist.rowScheduleTitle', 'Schedule item')}
          </DialogTitle>
          {rowLabel && (
            <DialogDescription className="truncate">
              {rowLabel}
            </DialogDescription>
          )}
        </DialogHeader>

        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={alwaysOn}
            onCheckedChange={v => setAlwaysOn(v === true)}
          />
          <span className="text-sm font-medium">
            {t('playlist.rowAlwaysOn', 'Show all the time')}
          </span>
        </label>

        <div className={`grid grid-cols-1 gap-3 transition-opacity ${alwaysOn ? 'opacity-50 pointer-events-none' : ''}`}>
          <label className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">{t('playlist.rowStart', 'Start')}</span>
            <input
              type="datetime-local"
              value={startAt}
              onChange={e => setStartAt(e.target.value)}
              disabled={alwaysOn}
              className="h-9 w-full min-w-0 rounded-md border border-border bg-background px-2 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">{t('playlist.rowEnd', 'End')}</span>
            <input
              type="datetime-local"
              value={endAt}
              onChange={e => setEndAt(e.target.value)}
              disabled={alwaysOn}
              className="h-9 w-full min-w-0 rounded-md border border-border bg-background px-2 text-sm"
            />
          </label>
          {rangeInvalid && (
            <p className="text-xs text-destructive">
              {t('playlist.rowRangeInvalid', 'End must be after start.')}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={submit} disabled={!canSave} className="gap-2">
            <Check className="w-4 h-4" />
            {t('common.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
